import React, { useState, useContext, useEffect, useRef } from 'react';
import { ViewContext } from 'components/lib';
import Tooltip from '@mui/material/Tooltip';

type EditableLabelProps = {
  label: string | null | undefined;
  item: Record<string, any>;
  keyToEdit?: 'name' | 'title' | 'description';
  onEdit: (value: string) => void;
  onSave?: (value: string) => Promise<unknown>;
  onSingleClick?: () => void;
  singleClickDelayMs?: number;
  useTruncation?: boolean;
  placeholder?: string;
  maxLength?: number;
};

const EditableLabel: React.FC<EditableLabelProps> = ({
  label,
  item,
  keyToEdit = 'name',
  onEdit,
  onSave,
  onSingleClick,
  singleClickDelayMs = 200,
  useTruncation = false,
  placeholder = '',
  maxLength,
}) => {
  const safeLabel = label ?? '';
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [draftValue, setDraftValue] = useState<string>(safeLabel);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const context = useContext(ViewContext);
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const previousLabelRef = useRef<string>(safeLabel);
  const singleClickTimeoutRef = useRef<number | null>(null);
  const isCommittingRef = useRef<boolean>(false);

  const handleDoubleClick = () => {
    if (singleClickTimeoutRef.current) {
      window.clearTimeout(singleClickTimeoutRef.current);
      singleClickTimeoutRef.current = null;
    }
    previousLabelRef.current = safeLabel;
    setDraftValue(safeLabel);
    setIsEditing(true);
  };

  const handleSingleClick = () => {
    if (!onSingleClick || isEditing) return;
    if (singleClickTimeoutRef.current) return;
    singleClickTimeoutRef.current = window.setTimeout(() => {
      singleClickTimeoutRef.current = null;
      onSingleClick();
    }, singleClickDelayMs);
  };

  const handleBlur = async () => {
    if (isCommittingRef.current) return;
    isCommittingRef.current = true;
    setIsEditing(false);

    if (!onSave) {
      isCommittingRef.current = false;
      return;
    }
    // Send updated label to the server

    const trimmedLabel = draftValue.trim();
    const originalLabel = previousLabelRef.current ?? '';
    const originalTrimmedLabel = originalLabel.trim();

    if (trimmedLabel.length === 0) {
      onEdit(previousLabelRef.current || '');
      context.notification.show(`New ${keyToEdit} must no be emtpy`, 'error', true, 'toast');
      isCommittingRef.current = false;
      return;
    }

    if (trimmedLabel !== originalTrimmedLabel) {
      try {
        await onSave(draftValue);
        item[keyToEdit] = draftValue;
        onEdit(draftValue);
        const itemLabel = item.name ?? item.title ?? keyToEdit;
        context.notification.show(`${itemLabel} ${keyToEdit} has been updated`, 'success', true, 'toast');
      } catch (error) {
        onEdit(previousLabelRef.current || '');
        context.handleError(error);
      }
    }
    isCommittingRef.current = false;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = typeof maxLength === 'number' ? e.target.value.slice(0, maxLength) : e.target.value;
    setDraftValue(nextValue);
  };

  useEffect(() => {
    if (!useTruncation || isEditing) return;

    const element = labelRef.current;
    if (!element) return;

    const checkTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    // Defer to ensure span is laid out after leaving edit mode
    requestAnimationFrame(checkTruncation);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(checkTruncation);
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
    }

    return;
  }, [safeLabel, isEditing, useTruncation]);

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(safeLabel);
    }
  }, [safeLabel, isEditing]);

  useEffect(() => {
    return () => {
      if (singleClickTimeoutRef.current) {
        window.clearTimeout(singleClickTimeoutRef.current);
        singleClickTimeoutRef.current = null;
      }
    };
  }, []);

  const labelStyle = useTruncation
    ? {
        display: 'inline-block',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
      }
    : undefined;

  const trimmedLabel = safeLabel.trim();
  const displayLabel = trimmedLabel ? safeLabel : placeholder;
  const labelContent = (
    <span
      ref={labelRef}
      onClick={handleSingleClick}
      onDoubleClick={handleDoubleClick}
      style={{
        cursor: 'text',
        color: item.deletedAt ? 'var(--mui-palette-text-disabled, #9e9e9e)' : undefined,
        ...labelStyle,
      }}
    >
      {displayLabel}
    </span>
  );

  const inputPlaceholder = isEditing && !trimmedLabel ? '' : placeholder;

  return isEditing ? (
    <input
      type="text"
      value={draftValue}
      onChange={handleChange}
      onBlur={handleBlur}
      autoFocus
      placeholder={inputPlaceholder}
      maxLength={maxLength}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className="editable-text-input"
    />
  ) : (
    <Tooltip
      title={useTruncation && trimmedLabel ? safeLabel : ''}
      disableHoverListener={!useTruncation || !isTruncated || isEditing}
      placement="top"
    >
      {labelContent}
    </Tooltip>
  );
};

export default EditableLabel;
